import React, { type MouseEvent } from 'react';

import styled, { css } from '@mint-lib/styled';

import { TableHeaderCell, Truncation } from '@myn/mui';

import { BUILDER_ROW_TYPES } from '../../../../../constants/campaignBuilder.js';
import { Columns } from '../../../../../constants/myCampaigns.js';
import { Header } from '../../../../../types/shared.js';
import ExpanderColumn from './ExpanderColumn.jsx';
import { StyledResizeBlock } from './HeaderDropZone.jsx';

type HeaderType = Header & {
  viewType?: string;
  isCalculated?: boolean;
  isPredictiveColumn?: boolean;
};

type Props = {
  header: HeaderType;
  isDisabled: boolean;
  expandedType?: BUILDER_ROW_TYPES;
  handleExpandRows: () => void;
  drag: any;
  scale: number;
  isPredictivePlanningMode: boolean;
};

const TableHeadItemContent: React.FC<Props> = ({
  header,
  isDisabled,
  expandedType,
  handleExpandRows,
  drag,
  scale,
  isPredictivePlanningMode,
}) => {
  const headerName = header.render?.('Header');
  const resizableProps = header.getResizerProps?.();
  const isDraggable =
    !header.isDragDisabled && !isDisabled && !isPredictivePlanningMode;
  const isHiddenColumn =
    header.id === Columns.MASK || header.id === Columns.ACTIONS_MENU;

  const handleResizeMouseDown = (e: MouseEvent) => {
    e.stopPropagation();
    resizableProps?.onMouseDown(e);
  };

  const handleTouchStart = (e: MouseEvent) => {
    e.stopPropagation();
    resizableProps?.onTouchStart(e);
  };

  return (
    <>
      {header.id === Columns.EXPANDER && !isDisabled && (
        <ExpanderColumn
          expandedType={expandedType}
          handleExpandRows={handleExpandRows}
          scale={scale}
        />
      )}
      {header.id !== Columns.EXPANDER && !isHiddenColumn && (
        <StyledHeaderCell
          onDrag={isDraggable ? () => null : undefined}
          ref={drag}
          align="right"
          scale={scale}
          isDraggable={isDraggable}
          isPredictive={isPredictivePlanningMode}
        >
          <StyledHeadTextNew
            tooltipTitle={headerName}
            isExpander={header.id === Columns.EXPANDER}
            isPredictive={header.isPredictiveColumn}
            variant="h6"
            scale={scale}
          >
            {headerName}
          </StyledHeadTextNew>
        </StyledHeaderCell>
      )}

      {!header.disableResizing && !isDisabled && !isHiddenColumn && (
        <StyledResizeBlock
          {...resizableProps}
          onMouseDown={handleResizeMouseDown}
          onTouchStart={handleTouchStart}
          onClick={(e: MouseEvent) => e.stopPropagation()}
          $scale={scale}
        />
      )}
    </>
  );
};

export default TableHeadItemContent;

const StyledHeaderCell = styled(TableHeaderCell)<{
  scale: number;
  isDraggable: boolean;
  isPredictive: boolean;
}>`
  display: flex;
  width: inherit;
  position: absolute;
  visibility: ${({ isDraggable, isPredictive }) =>
    isDraggable || isPredictive ? 'visible' : 'hidden'};

  height: ${({ scale }) => 40 * scale}px;

  & > div {
    max-width: 100%;
    height: 100%;
  }

  & svg {
    width: ${({ scale }) => 16 * scale}px;
    height: ${({ scale }) => 16 * scale}px;
  }
`;

const StyledHeadTextNew = styled(Truncation)<{
  isExpander: boolean;
  isPredictive?: boolean;
  scale: number;
}>`
  padding: ${({ scale }) => 10 * scale}px 0;
  ${({ isExpander, theme }) =>
    isExpander &&
    css`
      padding: ${theme.spacing(2)};
    `}
  ${({ scale, isPredictive, theme }) => css`
    font-size: ${0.875 * scale}rem;
    margin-left: ${5 * scale}px;
    color: ${isPredictive
      ? theme.palette.primary.dark
      : theme.palette.text.primary};
  `}

  /* Disable text selection */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;
